import {
  type DiscountPayload,
  type DiscountResponseSuccess,
  type ProductDiscount,
  checkDiscountRedeem,
  DISCOUNT_STATUS,
} from '@/api/discount';
import { type PlanBase } from '@/api/initial-state';

type PromoCodeState = {
  discountData: DiscountResponseSuccess | null;
};

export const usePromoCodeStore = defineStore('promo-code', {
  state(): PromoCodeState {
    return {
      discountData: null,
    };
  },

  getters: {
    codeValue(state) {
      return state.discountData?.code.value ?? '';
    },

    discountProducts(state) {
      if (!state.discountData) {
        return [];
      }

      const productIds = Object.entries(state.discountData.products)
        .map(([id, data]) => {
          if (data.status === DISCOUNT_STATUS.success) {
            return Number(id);
          }

          return null;
        })
        .filter(Boolean) as number[];

      return productIds;
    },
  },

  actions: {
    async applyCode(
      payload: DiscountPayload,
    ): Promise<{ success: true } | { success: false; error: string }> {
      try {
        const response = await checkDiscountRedeem(payload);

        if (response.code.status === DISCOUNT_STATUS.success) {
          this.discountData = markRaw(response as DiscountResponseSuccess);

          return {
            success: true,
          };
        }

        return {
          success: false,
          error: response.code.error,
        };
      } catch (_error) {
        this.discountData = null;

        return {
          success: false,
          error: 'Invalid promo code',
        };
      }
    },

    async applyMockCode(
      payload: DiscountPayload,
    ): Promise<{ success: true } | { success: false; error: string }> {
      try {
        if (payload.code !== 'free_trial') {
          return {
            success: false,
            error: 'Invalid promo code',
          };
        }

        const plansStore = usePlansStore();
        const productId = payload.product_ids[0];

        if (!productId || !plansStore.plans?.length) {
          return {
            success: false,
            error: 'Invalid promo code',
          };
        }

        const response = {
          code: {
            status: DISCOUNT_STATUS.success,
            value: 'free_trial',
          },
          products: {
            productId: plansStore.plans[0],
          },
        };

        this.discountData = markRaw(response as DiscountResponseSuccess);

        return { success: true };
      } catch (_error) {
        this.discountData = null;

        return {
          success: false,
          error: 'Invalid promo code',
        };
      }
    },

    removeCode() {
      this.discountData = null;
    },

    getProductDiscount(productId: number): ProductDiscount | null {
      if (!this.discountData) {
        return null;
      }

      const productData = this.discountData.products[productId];

      if (!productData || productData.status === DISCOUNT_STATUS.error) {
        return null;
      }

      const firstSuccessGateway = Object.values(productData.gateways).find(
        (gateway) => gateway.status === DISCOUNT_STATUS.success,
      );

      if (!firstSuccessGateway) {
        return null;
      }

      return firstSuccessGateway.info;
    },

    getDiscountMerchants(plan: PlanBase, merchants: number[]): number[] {
      if (!this.discountData) {
        return [];
      }

      const productData = this.discountData.products[plan['item-id']];

      if (!productData || productData.status === DISCOUNT_STATUS.error) {
        return [];
      }

      return Object.keys(productData.gateways)
        .map(Number)
        .filter((gatewayId) => {
          const canApply = plan['item-gateways'].includes(gatewayId);
          const isAvailable = merchants.includes(gatewayId);

          return canApply && isAvailable;
        });
    },
  },
});
